import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, PlayCircle, RefreshCw, X } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useRunFullAudit } from "@/hooks/agent/useBrandIntegrity";
import { useFullAuditProgress, type AuditPhase } from "@/hooks/agent/useFullAuditProgress";
import { Agent } from "@/types/agent/agent";
import {
  AUDIT_CATEGORIES,
  TOTAL_AUDIT_CATEGORIES,
  scoreBand,
} from "@/constants/brandAudit";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface FullAuditSheetProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent | null;
  onAuditStarted?: (testRunId: number) => void;
  onViewResults?: (testRunId: number) => void;
}

export const FullAuditSheet = ({
  isOpen,
  onClose,
  agent,
  onAuditStarted,
  onViewResults,
}: FullAuditSheetProps) => {
  const navigate = useNavigate();
  const runFullAudit = useRunFullAudit();
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const { state, generationPercent, executionPercent } = useFullAuditProgress(activeRunId);
  const resetMutation = runFullAudit.reset;

  const handleWatchLive = () => {
    if (!activeRunId) return;
    navigate(`/genie-hub/brand-integrity/preview?run=${activeRunId}`);
    onClose();
  };

  // Reset the local run when the sheet closes (the audit keeps running in the background).
  useEffect(() => {
    if (!isOpen) {
      setActiveRunId(null);
      setOptimisticPhase("idle");
      setScenariosPerCategory({});
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  const [scenariosPerCategory, setScenariosPerCategory] = useState<Record<string, number>>({});
  const [optimisticPhase, setOptimisticPhase] = useState<AuditPhase>("idle");

  const phase: AuditPhase = activeRunId
    ? (state.phase !== "idle" ? state.phase : optimisticPhase)
    : optimisticPhase !== "idle" ? optimisticPhase : "idle";

  const handleRun = async () => {
    if (!agent?.id) return;
    try {
      setOptimisticPhase("generating");
      const hasCustomCounts = AUDIT_CATEGORIES.some(
        (c) => (scenariosPerCategory[c.id] ?? c.defaultCount) !== c.defaultCount,
      );
      const res = await runFullAudit.mutateAsync({
        agentId: agent.id,
        ...(Object.keys(scenariosPerCategory).length > 0 && { scenariosPerCategory }),
        ...(hasCustomCounts && { regenerate: true }),
      });
      setActiveRunId(res.testRunId);
      onAuditStarted?.(res.testRunId);
    } catch {
      setOptimisticPhase("idle");
      // mutation surfaces error via runFullAudit.error
    }
  };

  const handleViewResults = () => {
    if (activeRunId) onViewResults?.(activeRunId);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl md:max-w-2xl flex flex-col p-0 bg-[#F9FAFC]"
      >
        <VisuallyHidden>
          <SheetTitle>Full Brand Audit</SheetTitle>
          <SheetDescription>
            Run a comprehensive Genie Trust Center audit tailored to your Genie.
          </SheetDescription>
        </VisuallyHidden>

        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-6 pb-5 bg-white border-b border-[#E5EAF0]">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <GenieIcon
                name="clipboard-check"
                size={40}
                className="h-10 w-10"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl md:text-2xl font-bold text-[#222B45] leading-tight">
                Full Brand Audit
              </h2>
              <p className="text-sm text-[#64748B] mt-1">
                {agent?.name
                  ? <>Tailored to <span className="font-semibold text-gray-900">{agent.name}</span> — we'll review every behaviour that matters.</>
                  : "A comprehensive review of your Genie's behaviour."}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Signature gradient hairline */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
          {phase === "idle" && (
            <PreRun
              agent={agent}
              error={runFullAudit.error?.message}
              scenariosPerCategory={scenariosPerCategory}
              onScenariosPerCategoryChange={setScenariosPerCategory}
            />
          )}
          {(phase === "generating" || phase === "generated" || phase === "executing") && (
            <InProgress
              state={state}
              generationPercent={generationPercent}
              executionPercent={executionPercent}
              phase={phase}
            />
          )}
          {phase === "completed" && (
            <CompletedState
              score={state.overallScore}
              testCount={state.totalTests || state.testsComplete}
              onViewResults={handleViewResults}
            />
          )}
          {phase === "failed" && (
            <FailedState
              message={state.errorMessage}
              onTryAgain={() => {
                setActiveRunId(null);
                runFullAudit.reset();
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-[#E5EAF0] bg-white px-6 sm:px-8 py-4 flex items-center justify-end gap-3">
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleRun}
                disabled={!agent?.id || runFullAudit.isPending}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100"
              >
                {runFullAudit.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting audit…
                  </>
                ) : (
                  "Run Full Audit"
                )}
              </Button>
            </>
          )}
          {(phase === "generating" || phase === "generated" || phase === "executing") && (
            <>
              <p className="text-xs text-gray-500 mr-auto">
                Audit continues in the background — watch it live or close this any time.
              </p>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button
                onClick={handleWatchLive}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-5 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
              >
                <PlayCircle className="h-4 w-4 mr-2" />
                Watch live
              </Button>
            </>
          )}
          {phase === "completed" && (
            <>
              <Button variant="outline" onClick={onClose}>Done</Button>
              <Button
                onClick={handleViewResults}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
              >
                View Detailed Results
              </Button>
            </>
          )}
          {phase === "failed" && (
            <Button variant="outline" onClick={onClose}>Close</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────

const PreRun = ({
  agent,
  error,
  scenariosPerCategory,
  onScenariosPerCategoryChange,
}: {
  agent: Agent | null;
  error?: string | null;
  scenariosPerCategory: Record<string, number>;
  onScenariosPerCategoryChange: (v: Record<string, number>) => void;
}) => {
  const totalScenarios = AUDIT_CATEGORIES.reduce(
    (sum, c) => sum + (scenariosPerCategory[c.id] ?? c.defaultCount),
    0,
  );

  const handleCategoryCount = (id: string, raw: string) => {
    if (raw === "") {
      const next = { ...scenariosPerCategory };
      delete next[id];
      onScenariosPerCategoryChange(next);
    } else {
      const n = Math.max(1, Math.min(20, parseInt(raw, 10)));
      if (!isNaN(n)) onScenariosPerCategoryChange({ ...scenariosPerCategory, [id]: n });
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero summary card — signature gradient border */}
      <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[14px] bg-white p-5 md:p-6">
          <div className="flex items-start gap-4">
            <GenieIcon
              name="sparkles"
              size={40}
              className="h-10 w-10 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-gray-900">
                Tailored to your Genie
              </p>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                We'll read {agent?.name ?? "your Genie"}'s setup — prompt, knowledge base, goal,
                tone — and generate <span className="font-semibold text-gray-900">{totalScenarios}</span> bespoke
                scenarios across {TOTAL_AUDIT_CATEGORIES} review areas. Every scenario is written for this Genie specifically.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What we test — category grid */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">What we'll review</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {AUDIT_CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-[#E5EAF0]"
            >
              <span className="mt-0.5 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 leading-tight">{cat.label}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-tight">{cat.blurb}</p>
              </div>
              <input
                type="number"
                min={1}
                max={20}
                value={scenariosPerCategory[cat.id] ?? cat.defaultCount}
                onChange={(e) => handleCategoryCount(cat.id, e.target.value)}
                className="w-12 flex-shrink-0 rounded-md border border-[#E5EAF0] px-1.5 py-1 text-sm text-center text-gray-900 focus:outline-none focus:ring-2 focus:ring-genie-primary/30 focus:border-genie-primary"
              />
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-2xl bg-white border border-[#E5EAF0] p-5 md:p-6">
        <p className="text-sm font-semibold text-gray-700 mb-3">How it works</p>
        <ul className="space-y-2.5 text-sm text-gray-600">
          <li className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-genie-primary/10 text-[11px] font-bold text-genie-primary">1</span>
            <span>Each scenario runs as a real conversation with your Genie.</span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-genie-primary/10 text-[11px] font-bold text-genie-primary">2</span>
            <span>You'll get an overall score, a category breakdown, and the exact transcripts where it slipped.</span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-genie-primary/10 text-[11px] font-bold text-genie-primary">3</span>
            <span>Audits typically finish in 3–5 minutes. You can close this and we'll notify you.</span>
          </li>
        </ul>
      </div>

      {error && (
        <p className="text-sm text-red-600 px-1">{error}</p>
      )}
    </div>
  );
};

const InProgress = ({
  state,
  generationPercent,
  executionPercent,
  phase,
}: {
  state: ReturnType<typeof useFullAuditProgress>["state"];
  generationPercent: number;
  executionPercent: number;
  phase: AuditPhase;
}) => {
  const isGenerating = phase === "generating";
  const isExecuting = phase === "executing" || phase === "generated";

  return (
    <div className="space-y-6">
      {/* Phase 1 — Generating */}
      <PhaseRow
        active={isGenerating}
        done={!isGenerating}
        title="Tailoring your audit"
        subtitle={
          state.usedCache
            ? `Reusing a recent suite of ${state.scenariosGenerated} scenarios`
            : isGenerating
              ? `${state.categoriesComplete} of ${state.totalCategories || TOTAL_AUDIT_CATEGORIES} areas tailored${state.scenariosGenerated > 0 ? ` · ${state.scenariosGenerated} scenarios ready` : ""}`
              : `${state.scenariosGenerated || state.totalTests} scenarios tailored to your Genie`
        }
        percent={isGenerating ? generationPercent : 100}
      />

      {/* Phase 2 — Running */}
      <PhaseRow
        active={isExecuting}
        done={false}
        muted={!isExecuting}
        title="Running scenarios"
        subtitle={
          isExecuting
            ? `${state.testsComplete} of ${state.totalTests || "?"} conversations complete`
            : "Waiting for tailoring to finish"
        }
        percent={isExecuting ? executionPercent : 0}
      />

      {/* Friendly waiting hint */}
      <div className="rounded-2xl bg-white border border-[#E5EAF0] p-5 text-sm text-gray-600 leading-relaxed">
        <p className="font-medium text-gray-900 mb-1">You can leave this open or close it.</p>
        Your audit keeps running in the background. You'll get a notification here when results are ready.
      </div>
    </div>
  );
};

export const PhaseRow = ({
  active,
  done,
  muted,
  title,
  subtitle,
  percent,
}: {
  active: boolean;
  done: boolean;
  muted?: boolean;
  title: string;
  subtitle: string;
  percent: number;
}) => {
  return (
    <div
      className={`rounded-2xl border p-5 md:p-6 transition-all ${
        active
          ? "border-transparent bg-white shadow-sm ring-2 ring-genie-primary/20"
          : done
            ? "border-[#E5EAF0] bg-white"
            : "border-[#E5EAF0] bg-white/60"
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-genie-primary flex-shrink-0" />
        ) : active ? (
          <Loader2 className="h-5 w-5 animate-spin text-genie-primary flex-shrink-0" />
        ) : (
          <span className="h-5 w-5 rounded-full border-2 border-gray-200 flex-shrink-0" />
        )}
        <p className={`text-base font-semibold ${muted ? "text-gray-400" : "text-gray-900"}`}>
          {title}
        </p>
      </div>
      <p className={`text-sm mb-3 ${muted ? "text-gray-400" : "text-gray-600"}`}>{subtitle}</p>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-[width] duration-500 ease-out"
          style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
        />
      </div>
    </div>
  );
};

const CompletedState = ({
  score,
  testCount,
  onViewResults,
}: {
  score: number | null;
  testCount: number;
  onViewResults: () => void;
}) => {
  const band = scoreBand(score);
  const bandLabel = useMemo(() => {
    if (band === "strong") return "Strong";
    if (band === "watch") return "Worth a look";
    if (band === "risk") return "Needs attention";
    return "Result";
  }, [band]);

  return (
    <div className="space-y-6">
      {/* Score badge */}
      <div className="flex flex-col items-center text-center py-4">
        <div className="relative h-40 w-40">
          {/* Gradient ring */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
          <div className="absolute inset-[6px] rounded-full bg-white" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-5xl font-bold text-[#222B45] leading-none">
              {score ?? "—"}
            </p>
            <p className="text-xs uppercase tracking-wider text-[#64748B] mt-1">out of 100</p>
          </div>
        </div>
        <p className="mt-5 text-lg font-semibold text-[#222B45]">{bandLabel}</p>
        <p className="text-sm text-[#64748B] mt-1">
          Across {testCount} tailored scenarios
        </p>
      </div>

      <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <div className="rounded-[14px] bg-white p-5 md:p-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">What's next</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Open the detailed results to see exactly which scenarios slipped, read the transcripts,
            and get prescriptive recommendations you can paste into your Genie's setup.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E5EAF0] p-5 md:p-6">
        <div className="flex items-start gap-4">
          <GenieIcon
            name="files"
            size={32}
            className="h-8 w-8 flex-shrink-0"
          />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Executive PDF</p>
            <p className="text-xs text-gray-500 mt-1">
              The branded one-click report lands next. For now, open the detailed results below.
            </p>
          </div>
          <Button variant="outline" size="sm" disabled className="flex-shrink-0">
            Coming soon
          </Button>
        </div>
      </div>

      <button
        onClick={onViewResults}
        className="text-sm text-genie-primary hover:underline font-medium w-full text-center"
      >
        View detailed results →
      </button>
    </div>
  );
};

const FailedState = ({
  message,
  onTryAgain,
}: {
  message: string | null;
  onTryAgain: () => void;
}) => {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 md:p-6">
        <p className="text-sm font-semibold text-red-700 mb-1">The audit couldn't complete</p>
        <p className="text-sm text-red-600">{message ?? "Something went wrong while running your audit."}</p>
      </div>
      <button
        onClick={onTryAgain}
        className="inline-flex items-center gap-2 text-sm text-genie-primary hover:underline font-medium"
      >
        <RefreshCw className="h-4 w-4" />
        Try the audit again
      </button>
    </div>
  );
};
